/**
 * Meu perfil (`/app/perfil`) — a conta de quem está logado, e só dela.
 *
 * Três blocos: os dados (nome, e-mail, função), a troca de senha e o resumo do
 * acesso. Dois campos NÃO são editáveis, e cada um diz por quê em vez de
 * aparecer como controle morto:
 *
 *   * o e-mail, porque é a credencial de login e precisa ser confirmado antes
 *     de valer — trocar sem confirmar deixaria a conta com um endereço nunca
 *     verificado, e a recuperação de senha passaria a apontar para ele;
 *   * a função, porque quem muda papel é a gestão de usuários. Um `select`
 *     desabilitado com "Administrador" dentro ensinaria que a pessoa pode se
 *     promover e depois recusaria — pior que não ter o controle.
 *
 * Nada aqui autoriza coisa nenhuma (CONSTITUICAO.md §4.1): o servidor tira o
 * id do usuário da SESSÃO, e nenhuma destas chamadas leva identificador.
 */
import { useEffect, useState, type ReactNode } from 'react'

import { Botao } from '../../componentes/Botao'
import { Campo, CampoSenha } from '../../componentes/Campos'
import { Icone, type NomeIcone } from '../../componentes/Icone'
import { Selo } from '../../componentes/Selo'
import { mostrarAviso } from '../../componentes/avisos'
import { ErroApi } from '../../plataforma/api'
import { useSessao } from '../../plataforma/contexto-sessao'
import {
  buscarPerfil,
  salvarPerfil,
  trocarPIN,
  trocarSenha,
  type Perfil,
} from '../../plataforma/perfil'
import { LayoutApp } from '../app/LayoutApp'

/** Erro por campo, no mesmo formato que a API devolve em `erro.campos`. */
type Erros = Record<string, string>

/** "há 2 dias" fica para depois: aqui a data exata é o que se confere. */
function dataHoraBR(iso: string | undefined): string {
  if (!iso) return '—'
  const quando = new Date(iso)
  if (Number.isNaN(quando.getTime())) return '—'
  return quando.toLocaleString('pt-BR', {
    day: '2-digit',
    month: '2-digit',
    year: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
  })
}

export function TelaMeuPerfil() {
  const { revalidar } = useSessao()

  const [perfil, setPerfil] = useState<Perfil | null>(null)
  const [falhaAoCarregar, setFalhaAoCarregar] = useState('')

  useEffect(() => {
    const cancelamento = new AbortController()

    buscarPerfil(cancelamento.signal)
      .then(setPerfil)
      .catch((causa: unknown) => {
        if (causa instanceof DOMException && causa.name === 'AbortError') return
        setFalhaAoCarregar(
          causa instanceof ErroApi ? causa.message : 'Não foi possível carregar o seu perfil.',
        )
      })

    return () => cancelamento.abort()
  }, [])

  if (falhaAoCarregar) {
    return (
      <LayoutApp largura="foco" titulo="Meu perfil" subtitulo="Seus dados de acesso">
        <div role="alert" className="rounded-sistema bg-erro-suave px-4 py-3 text-[13px] font-medium text-erro-texto">
          {falhaAoCarregar}
        </div>
      </LayoutApp>
    )
  }

  if (!perfil) {
    return (
      <LayoutApp largura="foco" titulo="Meu perfil" subtitulo="Seus dados de acesso">
        <div className="h-[280px] w-full animate-pulse rounded-sistema bg-superficie" />
      </LayoutApp>
    )
  }

  return (
    <LayoutApp largura="formulario" titulo="Meu perfil" subtitulo="Seus dados de acesso">
      <div className="flex w-full flex-col gap-5">
        <Identidade perfil={perfil} />

        <BlocoDeDados
          perfil={perfil}
          aoSalvar={(salvo) => {
            setPerfil(salvo)
            // O nome e as iniciais aparecem no rodapé da barra lateral, que lê
            // a sessão. Sem revalidar, a tela diz "salvo" e o avatar continua
            // com as iniciais antigas.
            void revalidar()
          }}
        />
        <BlocoDeSeguranca />
        <BlocoDeAcesso perfil={perfil} />
      </div>
    </LayoutApp>
  )
}

/* ---------------------------------------------------------- identidade */

/**
 * O cabeçalho de identidade — avatar, nome e a linha de contexto.
 *
 * Fica FORA de cartão de propósito: não é um bloco de dados a preencher, é
 * quem você é. Cartão em volta faria dele mais um formulário, e os três dados
 * que ele carrega (papel, empresa, desde quando) são justamente os que NÃO se
 * editam aqui.
 *
 * Isto substitui dois cartões "não editável" que ocupavam meia tela para dizer
 * "Administrador" e um e-mail. A informação é a mesma; o espaço, um quinto.
 */
function Identidade({ perfil }: { perfil: Perfil }) {
  const { situacao } = useSessao()
  const loja = situacao.estado === 'autenticado' ? situacao.sessao.loja : null

  return (
    <div className="flex flex-wrap items-center gap-4">
      <span className="flex h-[60px] w-[60px] shrink-0 items-center justify-center rounded-full bg-marca text-[20px] font-bold text-white">
        {perfil.iniciais}
      </span>
      <div className="min-w-0">
        <h2 className="font-titulo text-[24px] leading-tight font-semibold text-titulo">
          {perfil.nome}
        </h2>
        <div className="mt-1.5 flex flex-wrap items-center gap-x-2 gap-y-1 text-[12.5px] text-texto-suave">
          <span className="rounded-full bg-marca-suave px-2 py-[3px] text-[10.5px] font-bold tracking-[0.3px] text-marca-forte uppercase">
            {perfil.papel_rotulo}
          </span>
          {loja?.nome_fantasia || loja?.nome ? (
            <span>
              na <span className="font-semibold text-texto">{loja.nome_fantasia || loja.nome}</span>
            </span>
          ) : null}
          <span aria-hidden>·</span>
          <span>desde {mesEAnoBR(perfil.criado_em)}</span>
        </div>
      </div>
    </div>
  )
}

/** "fevereiro de 2026" — a precisão que "desde quando" pede. */
function mesEAnoBR(iso: string): string {
  const quando = new Date(iso)
  if (Number.isNaN(quando.getTime())) return '—'
  return quando.toLocaleDateString('pt-BR', { month: 'long', year: 'numeric' })
}

/* ------------------------------------------------------- dados pessoais */

function BlocoDeDados({
  perfil,
  aoSalvar,
}: {
  perfil: Perfil
  aoSalvar: (salvo: Perfil) => void
}) {
  const [nome, setNome] = useState(perfil.nome)
  const [erros, setErros] = useState<Erros>({})
  const [aviso, setAviso] = useState('')
  const [salvando, setSalvando] = useState(false)

  async function enviar() {
    setErros({})
    setAviso('')
    setSalvando(true)
    try {
      aoSalvar(await salvarPerfil({ nome }))
      mostrarAviso('Perfil salvo.')
    } catch (causa: unknown) {
      if (!(causa instanceof ErroApi)) {
        setAviso('Não foi possível salvar o seu perfil.')
        return
      }
      setErros(causa.campos)
      setAviso(causa.message)
    } finally {
      setSalvando(false)
    }
  }

  return (
    <Cartao numero="01" titulo="Dados pessoais">
      <form
        onSubmit={(evento) => {
          evento.preventDefault()
          void enviar()
        }}
        className="flex flex-col gap-4"
      >
        {aviso && (
          <div role="alert" className="rounded-sistema bg-erro-suave px-4 py-3 text-[13px] font-medium text-erro-texto">
            {aviso}
          </div>
        )}
        {/* Sucesso NÃO tem faixa aqui: vira aviso passageiro (`mostrarAviso`).
            Uma tarja verde permanente dentro do cartão ocupa espaço para
            sempre por causa de uma informação que valia dois segundos, e
            empurra o formulário para baixo toda vez que alguém salva. O erro
            fica, porque descreve algo que continua errado. */}

        <div className="grid gap-4 md:grid-cols-2">
          <Campo
            rotulo="Nome completo"
            value={nome}
            onChange={(evento) => setNome(evento.target.value)}
            placeholder="Ana Ribeiro"
            erro={erros.nome}
            dica="Aparece no avatar, no registro de auditoria e nos relatórios"
            required
          />

          {/*
            Função é DADO, e ocupa a segunda coluna em vez de um cartão
            próprio: quem muda papel é a gestão de usuários, e um `select`
            morto aqui ensinaria que a pessoa pode se promover.
          */}
          <DadoDeLeitura
            rotulo="Função"
            valor={perfil.papel_rotulo}
            dica={`${perfil.papel_descricao} Quem muda a função de alguém é a gestão de usuários, em Configurações — ninguém troca a própria.`}
          />
        </div>

        {/*
          O e-mail ocupa a linha inteira porque é o mais longo dos três, e
          porque a explicação embaixo dele é a que mais importa: é a credencial
          de login. Como dado e não como input cinza — um campo desabilitado
          sem explicação faz a pessoa tentar clicar, desistir e abrir chamado.
        */}
        <DadoDeLeitura
          rotulo="E-mail de acesso"
          sufixoDoRotulo="é o seu login"
          valor={perfil.email}
          dica="Para trocar o e-mail, fale com o suporte — a mudança exige confirmação antes de valer."
          selo={
            // `atencao` e não `erro`: e-mail por confirmar pede uma ação da
            // pessoa, não é uma falha do sistema (spec 01, cor reservada a
            // estado).
            <Selo tom={perfil.email_confirmado ? 'ok' : 'atencao'}>
              <Icone nome={perfil.email_confirmado ? 'check_circle' : 'warning'} tamanho={13} />
              {perfil.email_confirmado ? 'Confirmado' : 'Não confirmado'}
            </Selo>
          }
        />

        <div className="flex justify-end">
          <Botao type="submit" icone="check" disabled={salvando}>
            {salvando ? 'Salvando…' : 'Salvar'}
          </Botao>
        </div>
      </form>
    </Cartao>
  )
}

/**
 * Um dado que não se edita, com a forma de um campo.
 *
 * Tem rótulo em cima e caixa embaixo, na mesma altura e no mesmo raio dos
 * campos ao lado — é o que permite pôr um dado e um campo lado a lado numa
 * grade sem que a linha fique torta. O fundo levemente cinza e a ausência de
 * cursor de texto são o que diz "isto se lê".
 *
 * Substituiu um cartão bordado com título, valor, selo e um parágrafo de
 * explicação, que ocupava quatro vezes esta altura para dizer a mesma coisa.
 */
function DadoDeLeitura({
  rotulo,
  sufixoDoRotulo,
  valor,
  dica,
  selo,
}: {
  rotulo: string
  /** Aposto curto ao lado do rótulo — "é o seu login". */
  sufixoDoRotulo?: string
  valor: string
  dica?: string
  selo?: ReactNode
}) {
  return (
    <div className="min-w-0">
      <div className="mb-2 text-[13px] font-semibold text-texto">
        {rotulo}
        {sufixoDoRotulo && (
          <span className="font-normal text-texto-suave"> — {sufixoDoRotulo}</span>
        )}
      </div>
      <div className="flex h-[38px] items-center gap-2.5 rounded-sistema border border-borda bg-superficie-suave px-3.5">
        <Icone nome="lock" tamanho={15} className="shrink-0 text-texto-fraco" />
        <span className="min-w-0 flex-1 truncate text-[14px] text-tinta">{valor}</span>
        {selo}
      </div>
      {dica && <p className="mt-[6px] text-[12.5px] text-texto-fraco">{dica}</p>}
    </div>
  )
}

/* ---------------------------------------------------------- segurança */

/**
 * "02 · Segurança" — duas linhas independentes: senha e PIN de autorização.
 *
 * Cada linha abre o PRÓPRIO formulário (padrão do Garage) e vive num `<form>`
 * separado — as duas moram no mesmo cartão, mas nunca dentro de um `<form>`
 * comum, porque `<form>` aninhado é inválido em HTML e submeteria os dois de
 * uma vez.
 *
 * O PIN é a linha nova: o cartão "PIN de autorização" em Configurações vira
 * só um ponteiro para cá, em vez de duplicar o formulário — é o mesmo PIN de
 * 4 dígitos que o porteiro já valida no balcão (migração 0005), e cada um
 * define o PRÓPRIO, do mesmo jeito que cada um troca a própria senha.
 */
function BlocoDeSeguranca() {
  return (
    <Cartao numero="02" titulo="Segurança" subtitulo="Como você entra na loja">
      <div className="flex flex-col gap-5">
        <LinhaTrocarSenha />
        <div className="border-t border-divisoria pt-5">
          <LinhaTrocarPIN />
        </div>
      </div>
    </Cartao>
  )
}

/**
 * A linha "Senha" que ABRE o formulário, no padrão do Garage.
 *
 * Antes eram três campos de senha empilhados, sempre visíveis, cada um
 * ocupando a largura inteira do cartão. Trocar de senha é coisa que se faz uma
 * ou duas vezes por ano, e o formulário estava permanentemente aberto para uma
 * tarefa que quase nunca acontece — enquanto o que a pessoa vem ver aqui (nome,
 * e-mail, função) ficava espremido acima dele.
 *
 * Agora é uma LINHA com o que se precisa saber ("mantenha uma senha forte") e
 * um botão. Ao abrir, os três campos aparecem lado a lado, em três colunas: em
 * coluna única eles teriam 760 px cada, para caber oito caracteres.
 */
function LinhaTrocarSenha() {
  const [aberto, setAberto] = useState(false)
  const [atual, setAtual] = useState('')
  const [nova, setNova] = useState('')
  const [repetida, setRepetida] = useState('')
  const [erros, setErros] = useState<Erros>({})
  const [aviso, setAviso] = useState('')
  const [trocando, setTrocando] = useState(false)

  function fechar() {
    setAberto(false)
    setAtual('')
    setNova('')
    setRepetida('')
    setErros({})
    setAviso('')
  }

  async function enviar() {
    setErros({})
    setAviso('')
    setTrocando(true)
    try {
      const resultado = await trocarSenha({
        senha_atual: atual,
        nova_senha: nova,
        repetir_senha: repetida,
      })
      // O aviso vai para a barra, e não para dentro do cartão: o formulário
      // fecha junto, e uma faixa verde num bloco que acabou de sumir não seria
      // lida por ninguém.
      mostrarAviso(
        resultado.sessoes_encerradas > 0
          ? `${resultado.mensagem} ${resultado.sessoes_encerradas === 1 ? 'Outro acesso foi encerrado' : `Outros ${resultado.sessoes_encerradas} acessos foram encerrados`} — este continua aberto.`
          : `${resultado.mensagem} Nenhum outro acesso estava aberto.`,
      )
      fechar()
    } catch (causa: unknown) {
      if (!(causa instanceof ErroApi)) {
        setAviso('Não foi possível trocar a senha.')
        return
      }
      setErros(causa.campos)
      setAviso(causa.message)
    } finally {
      setTrocando(false)
    }
  }

  return (
    <form
      onSubmit={(evento) => {
        evento.preventDefault()
        void enviar()
      }}
      className="flex flex-col"
    >
      <LinhaDeSeguranca
        icone="lock"
        titulo="Senha"
        descricao={
          aberto
            ? 'Trocar a senha encerra os outros acessos e mantém este.'
            : 'Mantenha uma senha forte e exclusiva do Lumma Store.'
        }
        acao={
          aberto ? (
            <Botao variante="secundario" onClick={fechar}>
              Cancelar
            </Botao>
          ) : (
            <Botao variante="secundario" onClick={() => setAberto(true)}>
              Alterar senha
            </Botao>
          )
        }
      />

      {aberto && (
        <div className="flex flex-col gap-4 pt-4">
          {aviso && (
            <div role="alert" className="rounded-sistema bg-erro-suave px-4 py-3 text-[13px] font-medium text-erro-texto">
              {aviso}
            </div>
          )}

          {/* Três colunas a partir de `md`: cada campo guarda oito
              caracteres, e um deles com 700 px de largura é desproporção,
              não conforto. */}
          <div className="grid gap-4 md:grid-cols-3">
            <CampoSenha
              rotulo="Senha atual"
              autoComplete="current-password"
              value={atual}
              onChange={(evento) => setAtual(evento.target.value)}
              erro={erros.senha_atual}
            />

            {/* O medidor é orientação de UX; quem valida os 8 caracteres é o
                backend (CONSTITUICAO.md §4.4). */}
            <CampoSenha
              rotulo="Nova senha"
              autoComplete="new-password"
              comMedidor
              value={nova}
              onChange={(evento) => setNova(evento.target.value)}
              erro={erros.nova_senha}
            />

            <CampoSenha
              rotulo="Repetir a nova senha"
              autoComplete="new-password"
              value={repetida}
              onChange={(evento) => setRepetida(evento.target.value)}
              erro={erros.repetir_senha}
            />
          </div>

          <div className="flex justify-end">
            <Botao type="submit" icone="lock" disabled={trocando}>
              {trocando ? 'Trocando…' : 'Trocar senha'}
            </Botao>
          </div>
        </div>
      )}
    </form>
  )
}

/**
 * A linha "PIN de autorização" — mesmo padrão visual da linha de senha.
 *
 * Três dígitos digitados duas vezes (PIN e confirmação) mais a senha atual
 * como prova de identidade: a mesma exigência de segurança da troca de senha
 * (CONSTITUICAO.md §4.4). O PIN não abre sessão — ao contrário da senha, a
 * troca não derruba nenhum acesso, então não há aviso de "outros acessos
 * encerrados" aqui.
 */
function LinhaTrocarPIN() {
  const [aberto, setAberto] = useState(false)
  const [senhaAtual, setSenhaAtual] = useState('')
  const [pin, setPin] = useState('')
  const [repetido, setRepetido] = useState('')
  const [erros, setErros] = useState<Erros>({})
  const [aviso, setAviso] = useState('')
  const [trocando, setTrocando] = useState(false)

  function fechar() {
    setAberto(false)
    setSenhaAtual('')
    setPin('')
    setRepetido('')
    setErros({})
    setAviso('')
  }

  async function enviar() {
    setErros({})
    setAviso('')
    setTrocando(true)
    try {
      const resultado = await trocarPIN({
        senha_atual: senhaAtual,
        pin,
        repetir_pin: repetido,
      })
      mostrarAviso(resultado.mensagem)
      fechar()
    } catch (causa: unknown) {
      if (!(causa instanceof ErroApi)) {
        setAviso('Não foi possível definir o PIN.')
        return
      }
      setErros(causa.campos)
      setAviso(causa.message)
    } finally {
      setTrocando(false)
    }
  }

  // Só dígitos, no máximo 4 — o mesmo formato que o backend exige
  // (`porteiro.PINValido`). Filtrar aqui evita mandar "12a4" para o servidor
  // só para ele recusar por formato.
  function aoDigitarPIN(valor: string, aoMudar: (v: string) => void) {
    aoMudar(valor.replace(/\D/g, '').slice(0, 4))
  }

  return (
    <form
      onSubmit={(evento) => {
        evento.preventDefault()
        void enviar()
      }}
      className="flex flex-col"
    >
      <LinhaDeSeguranca
        icone="shield"
        titulo="PIN de autorização"
        descricao={
          aberto
            ? 'Confirme com a senha atual. O PIN não abre sessão — só autoriza desconto e ajuste no balcão.'
            : 'Os quatro dígitos que um gerente digita para liberar desconto e ajuste de estoque no balcão.'
        }
        acao={
          aberto ? (
            <Botao variante="secundario" onClick={fechar}>
              Cancelar
            </Botao>
          ) : (
            <Botao variante="secundario" onClick={() => setAberto(true)}>
              Definir/trocar PIN
            </Botao>
          )
        }
      />

      {aberto && (
        <div className="flex flex-col gap-4 pt-4">
          {aviso && (
            <div role="alert" className="rounded-sistema bg-erro-suave px-4 py-3 text-[13px] font-medium text-erro-texto">
              {aviso}
            </div>
          )}

          {/*
            Não é um `grid-cols-3`: três colunas iguais deixavam os dois
            campos de PIN (180px, `largura="curta"`) presos cada um no meio
            de uma coluna larga, com um vão enorme antes da próxima — os dois
            dígitos que formam UM par (PIN e confirmação) pareciam campos
            soltos. Aqui a senha ocupa o espaço que sobra e os dois PINs ficam
            colados, como o par que são.
          */}
          <div className="flex flex-col gap-4 md:flex-row md:items-start">
            <div className="md:flex-1">
              <CampoSenha
                rotulo="Senha atual"
                autoComplete="current-password"
                value={senhaAtual}
                onChange={(evento) => setSenhaAtual(evento.target.value)}
                erro={erros.senha_atual}
              />
            </div>

            <div className="flex gap-3">
              <Campo
                rotulo="Novo PIN"
                inputMode="numeric"
                autoComplete="off"
                maxLength={4}
                placeholder="0000"
                largura="curta"
                value={pin}
                onChange={(evento) => aoDigitarPIN(evento.target.value, setPin)}
                erro={erros.pin}
                dica="Exatamente 4 dígitos numéricos"
              />

              <Campo
                rotulo="Repetir o PIN"
                inputMode="numeric"
                autoComplete="off"
                maxLength={4}
                placeholder="0000"
                largura="curta"
                value={repetido}
                onChange={(evento) => aoDigitarPIN(evento.target.value, setRepetido)}
                erro={erros.repetir_pin}
              />
            </div>
          </div>

          <div className="flex justify-end">
            <Botao type="submit" icone="shield" disabled={trocando}>
              {trocando ? 'Salvando…' : 'Salvar PIN'}
            </Botao>
          </div>
        </div>
      )}
    </form>
  )
}

/**
 * Uma linha do bloco de segurança: ícone em tint, título, uma frase e a ação
 * à direita.
 *
 * É a forma do Garage, e vale a pena ser a mesma: as duas contas são da mesma
 * família, e quem administra as duas não deveria reaprender onde fica o botão
 * de trocar senha.
 */
function LinhaDeSeguranca({
  icone,
  titulo,
  descricao,
  acao,
}: {
  icone: NomeIcone
  titulo: string
  descricao: ReactNode
  acao: ReactNode
}) {
  return (
    <div className="flex flex-wrap items-center gap-3.5">
      <span className="flex h-9 w-9 shrink-0 items-center justify-center rounded-sistema bg-marca-suave text-marca">
        <Icone nome={icone} tamanho={18} />
      </span>
      <div className="min-w-0 flex-1 basis-[220px]">
        <div className="text-[13.5px] font-semibold text-tinta">{titulo}</div>
        <div className="mt-0.5 text-[12.5px] leading-[1.5] text-texto-suave">{descricao}</div>
      </div>
      <div className="shrink-0">{acao}</div>
    </div>
  )
}

/* -------------------------------------------------------------- acesso */

function BlocoDeAcesso({ perfil }: { perfil: Perfil }) {
  return (
    <Cartao numero="03" titulo="Acesso" subtitulo="Quando esta conta foi criada e usada pela última vez">
      <dl className="grid gap-4 sm:grid-cols-2">
        <div className="min-w-0">
          <dt className="text-[12.5px] text-texto-suave">Conta criada em</dt>
          <dd className="tabular mt-1 text-[13.5px] font-semibold text-tinta">
            {dataHoraBR(perfil.criado_em)}
          </dd>
        </div>
        <div className="min-w-0">
          <dt className="text-[12.5px] text-texto-suave">Último acesso</dt>
          <dd className="tabular mt-1 text-[13.5px] font-semibold text-tinta">
            {dataHoraBR(perfil.ultimo_acesso_em)}
          </dd>
        </div>
      </dl>
    </Cartao>
  )
}

/* --------------------------------------------------------------- peças */

/**
 * O cartão NUMERADO — a mesma forma de "Dados da empresa".
 *
 * O número não é enfeite: ele diz que os blocos têm ORDEM, e que esta é uma
 * tela de configuração percorrida de cima para baixo. É a forma que o Garage
 * usa, e as duas contas são da mesma família — quem administra as duas não
 * deveria reaprender onde ficam as coisas.
 *
 * O subtítulo é opcional porque nem todo bloco precisa de uma frase: "Dados
 * pessoais" se explica sozinho.
 */
function Cartao({
  numero,
  titulo,
  subtitulo,
  children,
}: {
  numero: string
  titulo: string
  subtitulo?: string
  children: ReactNode
}) {
  return (
    <section className="rounded-sistema border border-borda bg-superficie p-5 shadow-[var(--shadow-cartao)]">
      <div className="mb-5">
        <h2 className="font-titulo text-base font-semibold text-tinta">
          <span className="tabular text-marca-forte">{numero}</span>
          <span aria-hidden className="mx-1.5 text-inativo">
            ·
          </span>
          {titulo}
        </h2>
        {subtitulo && <p className="mt-[2px] text-[12.5px] text-texto-suave">{subtitulo}</p>}
      </div>
      {children}
    </section>
  )
}
